import type { SynergyEffect } from '../effects.js';
import type { Enemy, EntityPool, Tower } from '../entities.js';
import type { SimEvent } from '../events.js';
import type { Rng } from '../rng.js';
import { applyDamage } from './damage.js';
import type { SpatialGrid } from './spatial.js';
import { applyStatus, type StatusTuning } from './status.js';
import { isStatusActive } from '../statuses.js';
import type { DamageKind, EntityId, StatusKind } from '../types.js';

/**
 * Synergy hook evaluation (TRD-08 §5).
 *
 * The synergy engine COLLECTS effects onto the towers that grant them; this is where they
 * actually fire, at the pipeline stages TRD-05 §6 names. Keeping collection and execution
 * apart is what lets recompute stay a pure function of the board — it discovers what
 * should happen without needing a world to happen in.
 *
 * Everything here runs inside the damage pipeline, so it obeys the same rules: no
 * allocation in the common path, no wall-clock, and every RNG draw comes from the world's
 * seeded stream.
 */

export interface HookContext {
  tick: number;
  events: SimEvent[];
  enemies: EntityPool<Enemy>;
  towers: EntityPool<Tower>;
  grid: SpatialGrid;
  /** The world's shared scratch buffer — see SpatialGrid.query. */
  scratch: EntityId[];
  rng: Rng;
  tuning: StatusTuning;
  awardGold: (amount: number) => void;
  /**
   * Guards recursive detonations (R-08-11).
   *
   * Arcanist-6 detonating an enemy that kills a Marked enemy that detonates again is
   * intended for a couple of levels and catastrophic at twenty — a single wave could
   * cascade the whole field in one tick.
   */
  chainDepth: number;
}

const MAX_CHAIN_DEPTH = 3;

/**
 * Stage 10 — on-hit hooks.
 *
 * Runs after damage has been applied, so an execute sees the post-hit HP and a status
 * lands on an enemy that survived.
 */
export function applyOnHitHooks(ctx: HookContext, tower: Tower, enemy: Enemy): void {
  const hooks = tower.mods.onHitHooks;
  if (hooks.length === 0) return;

  for (let i = 0; i < hooks.length; i++) {
    const effect = hooks[i] as SynergyEffect;

    switch (effect.k) {
      case 'applyStatus': {
        if (!enemy.alive) break;
        applyStatus(
          enemy,
          {
            status: effect.status,
            magnitude: effect.magnitude,
            durationTicks: effect.durationTicks,
            stacks: effect.stacks ?? 1,
            sourceTower: tower.id,
          },
          ctx.tick,
          ctx.tuning,
        );
        ctx.events.push({
          t: 'statusApplied',
          enemy: enemy.id,
          status: effect.status,
          stacks: effect.stacks ?? 1,
          x: enemy.x,
          y: enemy.y,
        });
        break;
      }

      case 'executeBelow': {
        // Void-6. Marked here and resolved with every other death in system 10, so an
        // execute cannot mutate the enemy array mid-iteration (R-05-20).
        if (!enemy.alive) break;
        if (enemy.hp > enemy.maxHp * effect.percentMaxHp) break;
        enemy.hp = 0;
        enemy.alive = false;
        enemy.lastHitBy = tower.id;
        break;
      }

      // `bonusVsStatus` is already resolved by `vulnerabilityMultiplier` at damage stage 5
      // — the multiplier is a property of the TARGET's statuses, not of the attacker, so
      // reading it per-hook here would double-apply it.
      case 'bonusVsStatus':
        break;

      default:
        break;
    }
  }
}

export interface ShotModifiers {
  pierceAdd: number;
  chainAdd: number;
  damageMultiplier: number;
  kindOverride: DamageKind | null;
}

const NO_SHOT_MODS: ShotModifiers = {
  pierceAdd: 0,
  chainAdd: 0,
  damageMultiplier: 1,
  kindOverride: null,
};

/**
 * Stage 7 — on-fire hooks, evaluated once per shot.
 *
 * Returns modifiers for THIS shot only. Marksman-6's railshot pierces and doubles, but it
 * is a property of the projectile leaving the barrel, not of the tower — expressing it as
 * a persistent mod would make every subsequent shot a railshot.
 */
export function resolveShotModifiers(tower: Tower): ShotModifiers {
  const hooks = tower.mods.onFireHooks;
  if (hooks.length === 0) return NO_SHOT_MODS;

  let pierceAdd = 0;
  let chainAdd = 0;
  let damageMultiplier = 1;
  let kindOverride: DamageKind | null = null;

  const consume = (effect: SynergyEffect): void => {
    if (effect.k !== 'shotModifier') return;
    pierceAdd += effect.pierceAdd ?? 0;
    chainAdd += effect.chainAdd ?? 0;
    damageMultiplier *= effect.damageMultiplier ?? 1;
    if (effect.kindOverride) kindOverride = effect.kindOverride;
  };

  for (let i = 0; i < hooks.length; i++) {
    const effect = hooks[i] as SynergyEffect;

    if (effect.k === 'everyNthShot') {
      // `shotsFired` has already been incremented for this shot, so the Nth shot is the
      // one where the count divides evenly — the first shot of a run is shot 1, not 0.
      if (tower.shotsFired % effect.n !== 0) continue;
      for (const nested of effect.then) consume(nested);
      continue;
    }

    consume(effect);
  }

  if (pierceAdd === 0 && chainAdd === 0 && damageMultiplier === 1 && kindOverride === null) {
    return NO_SHOT_MODS;
  }
  return { pierceAdd, chainAdd, damageMultiplier, kindOverride };
}

/**
 * System 10 — on-kill hooks.
 *
 * Runs during death resolution, never inline at the moment of the killing blow, so an
 * explosion cannot mutate the array that `projectileMovement` is still walking.
 */
export function applyOnKillHooks(ctx: HookContext, tower: Tower, victim: Enemy): void {
  const hooks = tower.mods.onKillHooks;
  if (hooks.length === 0) return;
  runKillEffects(ctx, tower, victim, hooks);
}

function runKillEffects(
  ctx: HookContext,
  tower: Tower,
  victim: Enemy,
  effects: readonly SynergyEffect[],
): void {
  for (let i = 0; i < effects.length; i++) {
    const effect = effects[i] as SynergyEffect;

    switch (effect.k) {
      case 'onKillExplode': {
        if (ctx.chainDepth >= MAX_CHAIN_DEPTH) break;
        // Arcanist-6 only detonates a MARKED victim; without the guard it would turn
        // every kill into an explosion and the tier-4 Mark would be pointless.
        if (effect.requireStatus && !hasStatus(victim, effect.requireStatus, ctx.tick)) break;

        const damage = victim.maxHp * effect.percentMaxHp;
        ctx.grid.query(victim.x, victim.y, effect.radiusTiles, ctx.enemies, ctx.scratch);
        for (const id of ctx.scratch) {
          if (id === victim.id) continue;
          const other = ctx.enemies.get(id);
          if (!other) continue;
          applyDamage(
            other,
            { amount: damage, kind: effect.kind, crit: false, tower },
            ctx.tick,
            ctx.events,
          );
          if (!other.alive) other.lastHitBy = tower.id;
        }
        break;
      }

      case 'onKillSpreadStatus': {
        // Venom-6 only bursts a victim that was already saturated, which is what makes
        // stacking poison worth doing rather than a flat AoE.
        if (
          effect.requireStacks !== undefined &&
          victim.statuses.poison.stacks < effect.requireStacks
        ) {
          break;
        }

        ctx.grid.query(victim.x, victim.y, effect.radiusTiles, ctx.enemies, ctx.scratch);
        for (const id of ctx.scratch) {
          if (id === victim.id) continue;
          const other = ctx.enemies.get(id);
          if (!other) continue;
          applyStatus(
            other,
            {
              status: effect.status,
              magnitude: 0,
              durationTicks: 240,
              stacks: effect.stacks,
              sourceTower: tower.id,
            },
            ctx.tick,
            ctx.tuning,
          );
          ctx.events.push({
            t: 'statusApplied',
            enemy: other.id,
            status: effect.status,
            stacks: effect.stacks,
            x: other.x,
            y: other.y,
          });
        }
        break;
      }

      case 'chanceOnKill': {
        if (!ctx.rng.chance(effect.p)) break;
        runKillEffects(ctx, tower, victim, effect.then);
        break;
      }

      case 'gold': {
        if (effect.trigger !== 'kill') break;
        if (effect.chance !== undefined && !ctx.rng.chance(effect.chance)) break;
        ctx.awardGold(effect.amount);
        break;
      }

      default:
        break;
    }
  }
}

function hasStatus(enemy: Enemy, status: StatusKind, tick: number): boolean {
  switch (status) {
    case 'mark':
      return isStatusActive(enemy.statuses.mark.expiresAtTick, tick);
    case 'chill':
      return isStatusActive(enemy.statuses.chill.expiresAtTick, tick);
    case 'wither':
      return isStatusActive(enemy.statuses.wither.expiresAtTick, tick);
    case 'poison':
      return isStatusActive(enemy.statuses.poison.expiresAtTick, tick);
    case 'shock':
      return isStatusActive(enemy.statuses.shock.expiresAtTick, tick);
    case 'stun':
      return isStatusActive(enemy.statuses.stun.expiresAtTick, tick);
    case 'freeze':
      return isStatusActive(enemy.statuses.freeze.expiresAtTick, tick);
    default:
      return false;
  }
}
